/**
 * Sign-in logic for screens/SignInScreen.jsx (both looks since v1.71): email-or-username
 * + password, Google, the busy flags, the status line, and the fade-out that
 * runs before the post-login redirect.
 *
 * `exitDuration` lets a screen shorten the fade (Floodlight passes 0 under
 * reduced motion); Classic keeps the original 600ms.
 */
import { useRef, useState } from 'react';
import { Platform, Animated, Easing } from 'react-native';
import { useNavigation } from '../context/NavigationContext';
import { useAuth } from '../context/AuthContext';

/** On web, read the DOM value too: browser autofill can skip onChangeText. */
export const getInputValue = (inputRef, stateValue) => {
  if (Platform.OS === 'web' && inputRef?.current?.value != null) {
    return inputRef.current.value;
  }
  return stateValue;
};

export const UNEXPECTED_ERROR = 'An unexpected error occurred. Please try again.';

export default function useSignIn({ exitDuration = 600 } = {}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState(null);
  const [isExiting, setIsExiting] = useState(false);

  const pageOpacity = useRef(new Animated.Value(1)).current;
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const { navigateTo, handleLoginRedirect } = useNavigation();
  const { signIn, signInWithGoogleAuth } = useAuth();

  const isBusy = emailLoading || googleLoading || isExiting;

  const exitAnimation = (callback) => {
    setIsExiting(true);
    Animated.timing(pageOpacity, {
      toValue: 0,
      duration: exitDuration,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: true,
    }).start(() => {
      if (callback) callback();
    });
  };

  const fail = (message, setBusy) => {
    setStatus(message);
    setStatusType('error');
    setBusy(false);
  };

  const handleSignIn = async () => {
    if (isBusy) return;

    const resolvedEmail = getInputValue(emailRef, email).trim();
    const resolvedPassword = getInputValue(passwordRef, password);

    setEmailLoading(true);
    setStatus('');
    setStatusType(null);

    try {
      const { error } = await signIn(resolvedEmail, resolvedPassword);
      if (error) {
        fail(error, setEmailLoading);
      } else {
        exitAnimation(() => handleLoginRedirect());
      }
    } catch (error) {
      console.error('Email Sign-In Error:', error);
      fail(UNEXPECTED_ERROR, setEmailLoading);
    }
  };

  const handleGoogleSignIn = async () => {
    if (isBusy) return;

    setGoogleLoading(true);
    setStatus('');
    setStatusType(null);

    try {
      const { error } = await signInWithGoogleAuth();
      if (error) {
        fail(error, setGoogleLoading);
      } else {
        exitAnimation(() => handleLoginRedirect());
      }
    } catch (error) {
      console.error('Google Sign-In Error:', error);
      fail(UNEXPECTED_ERROR, setGoogleLoading);
    }
  };

  const handleForgotPassword = () => {
    if (isBusy) return;
    exitAnimation(() => navigateTo('ForgotPassword'));
  };

  const goToRegister = () => navigateTo('Register');

  return {
    email, setEmail, password, setPassword,
    emailLoading, googleLoading, isExiting, isBusy,
    status, statusType,
    pageOpacity, emailRef, passwordRef,
    handleSignIn, handleGoogleSignIn, handleForgotPassword, goToRegister,
  };
}
